import React, { useState, useEffect, useCallback, useMemo } from 'react';
import GoogleMapReact from 'google-map-react';
import CircularProgress from 'components/ProgressLoader';
import Marker from './Marker';
import GeoFence from './circularFence';
import { ReactComponent as Pin } from 'assets/svg/marker.svg';
import { ReactComponent as MyPin } from 'assets/svg/myLocationPin.svg';
import useSupercluster from 'use-supercluster';
import getValue from 'lodash/get';
import { ReactComponent as ClusterIcon } from 'assets/svg/cluster.svg';
import medicalMapStyle from './medicalMapStyle';
import { meters2ScreenPixels } from 'google-map-react/utils';

function SimpleMap(props) {
  const [zoomMap, setZoomMap] = useState(11);
  const [mapCenter, setMapCenter] = useState({ lat: 40.71427, lng: -74.00597 });
  const [mapLoading, setMapLoading] = useState(true);
  const [markerList, setMarkerList] = useState([]);
  const [bounds, setBounds] = useState(null);
  const mapRef = React.useRef();
  const {
    loading = false,
    onMapLoadCallback = () => {
      return false;
    },
    infoDisabled = false,
    data,
    accessor,
    getMarkerDisableState = () => {},
    showDetailButtonProps,
    infoAccessorList = [],
    clustering = true,
    clusterRadius = 60,
    fenceRadius,
    fenceLatLng,
    handleMarkerClick,
    activeId,
    activeItem,
    resetActive,
    zoom = zoomMap,
    setZoom = setZoomMap,
    center = mapCenter,
    setCenter = setMapCenter,
    noMarkerText = false,
    onSiteConnectClick = null,
    primaryColor = '#213a83',
    google_place_api_key = '',
  } = props;

  const points = useMemo(() => {
    const markerData = data.map((item, i) => {
      // return { index: (i + 1), lat: getValue(item, accessor.lat), lng: getValue(item, accessor.lng), ...item }
      return {
        type: 'Feature',
        properties: {
          cluster: false,
          index: i,
          ...item,
        },
        geometry: {
          type: 'Point',
          coordinates: [
            getValue(item, accessor.lng),
            getValue(item, accessor.lat),
          ],
        },
      };
    });

    return markerData;
  }, [data, accessor]);

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: clusterRadius, maxZoom: 20 },
  });
  const onMapLoad = ({ map }) => {
    mapRef.current = map;
    setMapLoading(false);
    onMapLoadCallback();
  };
  useEffect(() => {
    setMapLoading(loading);
  }, [loading]);

  useEffect(() => {
    !clustering && setMarkerList(data || []);
  }, [data, accessor, clustering]);

  useEffect(() => {
    const lat = getValue(activeItem, accessor.lat);
    const lng = getValue(activeItem, accessor.lng);
    if (lat && lng) {
      const center = { lat, lng };
      setCenter(center);
    }
  }, [activeItem, accessor, setCenter]);

  const handleChange = ({ zoom, center, bounds }) => {
    setZoom(zoom);
    setCenter(center);
    if (clustering) {
      setBounds([bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat]);
    }
  };
  const onMarkerClick = (id, item) => {
    setCenter({ lat: item.lat, lng: item.lng });
    handleMarkerClick(id, item);
  };
  const onClusterClick = React.useCallback(
    (cluster_id, location) => {
      const expansionZoom = Math.min(
        supercluster.getClusterExpansionZoom(cluster_id),
        20,
      );
      // setZoom(expansionZoom)
      // setCenter(location)
      mapRef.current.setZoom(expansionZoom);
      mapRef.current.panTo({ lat: location.lat, lng: location.lng });
    },
    [supercluster, mapRef],
  );

  const createMapOptions = useMemo(() => {
    return {
      styles: medicalMapStyle,
      clickableIcons: false,
    };
  }, []);

  const getGeofenceDimension = useCallback(
    (radiusInMtr) => {
      return radiusInMtr && meters2ScreenPixels(radiusInMtr, center, zoom);
    },
    [center, zoom],
  );

  const fenceRadiusInPx = useMemo(() => getGeofenceDimension(fenceRadius), [
    getGeofenceDimension,
    fenceRadius,
  ]);
  return (
    <GoogleMapReact
      bootstrapURLKeys={{
        key: google_place_api_key,
        libraries: ['places'].join(','),
      }}
      zoom={zoom}
      center={center}
      options={createMapOptions}
      onChange={handleChange}
      onGoogleApiLoaded={onMapLoad}
      yesIWantToUseGoogleMapApiInternals
    >
      {mapLoading && (
        <CircularProgress type="circular" size={40} color="primary" />
      )}
      {!mapLoading && fenceRadius && (
        <GeoFence
          lat={fenceLatLng.lat}
          lng={fenceLatLng.lng}
          radius={fenceRadiusInPx.w}
        />
      )}
      {fenceLatLng && fenceLatLng.lat ? (
        <Marker
          lat={fenceLatLng.lat}
          lng={fenceLatLng.lng}
          icon={{ color: '#005159', svg: MyPin, fontSize: 'large' }}
          disabled={true}
          disabledInfo={true}
        />
      ) : (
        ''
      )}
      {/* {!mapLoading && true && <Cluster {...{ markerList: clusters, accessor, activeId, onMarkerClick }} />} */}
      {/* {!mapLoading && data.map((item, index) => {
      const lat = getValue(item, accessor.lat)
      const lng = getValue(item, accessor.lng)
      const id = getValue(item, accessor.id)
      const isActive = id === activeId
      const isDisabled = getMarkerDisableState(item)
      const disabledInfo = infoDisabled || isDisabled

      return <Marker
        lat={lat}
        lng={lng}
        icon={{ color: isActive ? "#005159" : isDisabled?"#ccc":"#213a83", svg: Pin }}
        text={(index + 1)}
        info={item}
        isActive = {isActive}
        disabled = {isDisabled}
        disabledInfo = {disabledInfo}
        showDetail={disabledInfo?false:isActive}
        infoAccessorList={infoAccessorList}
        onClick={() => {!isDisabled && onMarkerClick(id, item);}}
        key={id}
      />
    })} */}
      {clusters.map((item, i) => {
        const { properties = {}, geometry = {} } = item || {};
        const [lng, lat] = geometry.coordinates || [];

        if (!properties.cluster) {
          const id = getValue(properties, accessor.id);
          const isDisabled = getMarkerDisableState(properties);
          const disabledInfo = infoDisabled || isDisabled;
          const isActive = activeId === id;
          const { index } = properties;
          const link = getValue(properties, accessor.link);
          const trialId = getValue(properties, accessor.trialId);

          return (
            <Marker
              lat={lat}
              lng={lng}
              icon={{
                color: isActive ? '#005159' : isDisabled ? '#ccc' : primaryColor,
                svg: Pin,
              }}
              text={!noMarkerText && index + 1}
              info={properties}
              isActive={isActive}
              disabled={isDisabled}
              disabledInfo={disabledInfo}
              showDetail={disabledInfo ? false : isActive}
              infoAccessorList={infoAccessorList}
              onClick={() => {
                !isDisabled && onMarkerClick(id, item);
              }}
              resetActive={resetActive}
              showDetailButtonProps={{ 
                ...showDetailButtonProps,
                href: onSiteConnectClick ? '#' : link,
                onClick: onSiteConnectClick ? (e) =>{
                  onSiteConnectClick(
                        { trialId, site: properties, distanceUnit: null },
                        e,
                      )
                    }
                  : null
              }}
              key={id}
            />
          );
        } else {
          return (
            <Marker
              lat={lat}
              lng={lng}
              radius={40 + (properties.point_count / points.length) * 50}
              icon={{ color: primaryColor, svg: ClusterIcon }}
              text={properties.point_count}
              align="center"
              disabledInfo={true}
              key={properties.cluster_id}
              onClick={() =>
                onClusterClick(properties.cluster_id, { lat, lng })
              }
            />
          );
        }
      })}
    </GoogleMapReact>
  );
}

export default SimpleMap;
